import { useEffect, useState } from "react";
import PageHeader from "../../components/common/pageheader/PageHeader";
import { createStudent, disableStudent, getStudents, updateStudent } from "../../services/studentsApi";
import StudentFormModal from "./studentFormModal/StudentFormModal";
import StudentSubjectsModal from "./studentSubjectModal/StudentSubjectsModal";

import { FiBook, FiEye } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ActionsMenu from "../../components/common/actionsMenu/ActionsMenu";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal";
import Loader from "../../components/common/Loader/Loader";
import TableEmptyState from "../../components/common/tableEmptyState/TableEmptyState";

import { useAuth } from "../../context/AuthContext";

import styles from "./StudentPanelPage.module.css";

const PHONE_LOCAL_REGEX = /^[1-9]\d{8}$/;
const stripPhonePrefix = (stored) => {
  if (!stored) return "";
  return stored.startsWith("+994") ? stored.slice(4) : stored;
};

const StudentPanelPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // View-only roles (TEACHER) can browse students but not create/edit/disable/assign them.
  const canManageStudents = (user?.roles || []).some((r) => ["ADMIN", "OWNER", "COORDINATOR"].includes(r));
  const [students, setStudents] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [filters, setFilters] = useState({
    studentNumber: "",
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [includeDisabled, setIncludeDisabled] = useState(false);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [formError, setFormError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  const [formValues, setFormValues] = useState({
    id: null,
    studentNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    nationalId: "",
    birthDate: "",
    gender: "",
    address: "",
  });
  const [saving, setSaving] = useState(false);

  const [isSubjectsModalOpen, setIsSubjectsModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const navigate = useNavigate();

  const fetchStudents = async (overridePageNo) => {
    try {
      setLoading(true);
      setListError("");

      const currentPage = overridePageNo ?? pageNo;

      const baseFilters = {
        pageNo: currentPage,
        pageSize,
      };

      const cleanedFilters = { ...baseFilters };

      ["studentNumber", "firstName", "lastName", "phone"].forEach((key) => {
        const value = filters[key];
        if (value && value.trim().length > 0) {
          cleanedFilters[key] = value.trim();
        }
      });

      if (includeDisabled) {
        cleanedFilters.includeDisabled = true;
      }

      const applied = {};
      ["studentNumber", "firstName", "lastName", "phone"].forEach((key) => {
        if (cleanedFilters[key]) applied[key] = cleanedFilters[key];
      });
      setAppliedFilters(applied);

      const data = await getStudents(cleanedFilters);

      setStudents(data.content || []);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.students.loadError");

      setListError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, includeDisabled]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const next = name === "phone" ? value.replace(/\D/g, "") : value;
    setFilters((prev) => ({
      ...prev,
      [name]: next,
    }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPageNo(1);
    fetchStudents(1);
  };

  const resetFormValues = () => {
    setFormValues({
      id: null,
      studentNumber: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      nationalId: "",
      birthDate: "",
      gender: "",
      address: "",
    });
  };

  const openCreateModal = () => {
    setFormMode("create");
    resetFormValues();
    setFormError("");
    setIsModalOpen(true);
  };

  const openEditModal = (student) => {
    setFormMode("edit");
    setFormValues({
      id: student.id,
      studentNumber: student.studentNumber,
      firstName: student.firstName || "",
      lastName: student.lastName || "",
      email: student.email || "",
      phone: stripPhonePrefix(student.phone),
      nationalId: student.nationalId || "",
      birthDate: student.birthDate || "",
      gender: student.gender || "",
      address: student.address || "",
    });
    setFormError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetFormValues();
    setFormError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError("");

    try {
      if (!formValues.firstName.trim() || !formValues.lastName.trim()) {
        setFormError(t("pages.students.firstAndLastNameRequired"));
        setSaving(false);
        return;
      }

      if (!formValues.email.trim()) {
        setFormError(t("pages.students.emailRequired"));
        setSaving(false);
        return;
      }

      // Gender and phone are optional. Validate the phone format only when one is entered.
      const phoneLocal = (formValues.phone || "").trim();
      if (phoneLocal && !PHONE_LOCAL_REGEX.test(phoneLocal)) {
        setFormError(t("validation.phoneFormat"));
        setSaving(false);
        return;
      }

      const payload = {
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        email: formValues.email.trim(),
        studentNumber: formValues.studentNumber,
        phone: phoneLocal ? `+994${phoneLocal}` : null,
        nationalId: formValues.nationalId?.trim() || null,
        birthDate: formValues.birthDate || null,
        gender: formValues.gender || null,
        address: formValues.address?.trim() || null,
      };

      if (formMode === "create") {
        await createStudent(payload);
        toast.success(t("pages.students.createSuccess"), { toastId: "student-create-success" });
      } else {
        await updateStudent(payload);
        toast.success(t("pages.students.updateSuccess"), { toastId: "student-update-success" });
      }

      closeModal();
      fetchStudents();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.students.operationFailed");

      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDisableConfirm = (student) => {
    setStudentToDelete(student);
    setIsDeleteOpen(true);
  };

  const handleConfirmDisable = async () => {
    if (!studentToDelete) return;

    try {
      setLoading(true);
      setListError("");
      await disableStudent(studentToDelete.studentNumber);
      toast.success(t("pages.students.disableSuccess"));

      setIsDeleteOpen(false);
      setStudentToDelete(null);
      fetchStudents();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.students.disableFailed");

      setListError(msg);
      setIsDeleteOpen(false);
      setStudentToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDisable = () => {
    setIsDeleteOpen(false);
    setStudentToDelete(null);
  };

  const openSubjectsModal = (student) => {
    setSelectedStudent(student);
    setIsSubjectsModalOpen(true);
  };

  const closeSubjectsModal = () => {
    setIsSubjectsModalOpen(false);
    setSelectedStudent(null);
  };

  const handleSubjectsAssigned = async () => {
    await fetchStudents();
    toast.success(t("pages.students.subjectsAssignedSuccess"));
  };

  return (
    <div className={styles.container}>
      <PageHeader title={t("pages.students.title")} />

      <form onSubmit={handleFilterSubmit} className={styles.filterForm}>
        <input
          type="text"
          name="studentNumber"
          placeholder={t("pages.students.searchByStudentNumber")}
          value={filters.studentNumber}
          onChange={handleFilterChange}
          className={styles.filterInput}
        />

        <input
          type="text"
          name="firstName"
          placeholder={t("pages.students.searchByFirstName")}
          value={filters.firstName}
          onChange={handleFilterChange}
          className={styles.filterInput}
        />

        <input
          type="text"
          name="lastName"
          placeholder={t("pages.students.searchByLastName")}
          value={filters.lastName}
          onChange={handleFilterChange}
          className={styles.filterInput}
        />

        <input
          type="text"
          name="phone"
          placeholder={t("pages.students.searchByPhone")}
          value={filters.phone}
          onChange={handleFilterChange}
          className={styles.filterInput}
        />

        <button type="submit" className={styles.primaryBtn}>
          {t("common.search")}
        </button>

        {canManageStudents && (
          <button type="button" onClick={openCreateModal} className={styles.secondaryBtn}>
            {t("pages.students.addStudent")}
          </button>
        )}

        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => {
              setIncludeDisabled(e.target.checked);
              setPageNo(1);
            }}
          />
          {t("pages.students.showDisabled")}
        </label>
      </form>

      {loading && <Loader fullscreen />}
      {listError && <p className={styles.error}>{t("common.errorLabel")}: {listError}</p>}

      {!loading && students.length === 0 && !listError && (
        <TableEmptyState
          title={t("pages.students.noStudentsFound")}
          description={t("pages.students.noStudentsDescription")}
          icon={Object.keys(appliedFilters).length > 0 ? "search" : "inbox"}
        />
      )}

      {!loading && students.length > 0 && (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("pages.students.studentNumberShort")}</th>
                <th>{t("pages.students.firstName")}</th>
                <th>{t("pages.students.lastName")}</th>
                <th>{t("pages.students.email")}</th>
                <th>{t("pages.students.phone")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.studentNumber} className={s.disabled ? styles.disabledRow : undefined}>
                  <td>{s.studentNumber}</td>
                  <td>
                    {s.firstName}
                    {s.disabled && (
                      <span className={styles.disabledBadge}>{t("pages.students.disabledBadge")}</span>
                    )}
                  </td>
                  <td>{s.lastName}</td>
                  <td>{s.email}</td>
                  <td>{s.phone}</td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actions}>
                      {s.disabled ? (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => navigate(`/students/${s.studentNumber}`)}
                            className={styles.actionBtn}
                            title={t("pages.students.viewDetails")}
                          >
                            <FiEye size={16} />
                            <span>{t("pages.students.detailsAction")}</span>
                          </button>

                          {canManageStudents && (
                            <>
                              <button
                                type="button"
                                onClick={() => openSubjectsModal(s)}
                                className={styles.actionBtn}
                                title={t("pages.students.assignSubject")}
                              >
                                <FiBook size={16} />
                                <span>{t("pages.students.subjectsAction")}</span>
                              </button>

                              <ActionsMenu
                                onEdit={() => openEditModal(s)}
                                editDisabled={s.hasParticipatedInExam}
                                editDisabledHint={t("pages.students.editLockedAfterExam")}
                                onDisable={() => openDisableConfirm(s)}
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <button
              type="button"
              onClick={() => setPageNo((p) => Math.max(p - 1, 1))}
              disabled={pageNo === 1}
              className={styles.paginationBtn}
            >
              {t("pages.students.previous")}
            </button>

            <span className={styles.paginationInfo}>
              {t("common.pageOf", { page: pageNo, total: totalPages || 1 })}
            </span>

            <button
              type="button"
              onClick={() => setPageNo((p) => (totalPages && p < totalPages ? p + 1 : p))}
              disabled={totalPages && pageNo >= totalPages}
              className={styles.paginationBtn}
            >
              {t("pages.students.next")}
            </button>
          </div>
        </>
      )}

      <StudentFormModal
        isOpen={isModalOpen}
        mode={formMode}
        values={formValues}
        onChange={handleFormChange}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        saving={saving}
        error={formError}
      />

      <StudentSubjectsModal
        isOpen={isSubjectsModalOpen}
        student={selectedStudent}
        onClose={closeSubjectsModal}
        onAssigned={handleSubjectsAssigned}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onConfirm={handleConfirmDisable}
        onCancel={handleCancelDisable}
        forceDelete
        confirmLabel={t("common.disable")}
        message={
          studentToDelete
            ? t("pages.students.disableConfirm", { firstName: studentToDelete.firstName, lastName: studentToDelete.lastName })
            : t("pages.students.deleteConfirmFallback")
        }
      />
    </div>
  );
};

export default StudentPanelPage;
